import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	effect,
	OnInit,
	signal,
	ViewChild
} from '@angular/core';
import {
	FormBuilder,
	FormGroup,
	FormsModule,
	ReactiveFormsModule
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { ProfileService } from './services/profile.service';
// ngrx setup
import { CommonModule } from '@angular/common';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { MaterialModule } from '@app/material.module';
import * as ProfileActions from '@app/store/actions/profile.actions';
import { selectProfileData } from '@app/store/selectors/profile.selectors';
import { AppState } from '@app/store/state';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { SelectDropdownComponent } from '../select-dropdown/select-dropdown.component';
import { SharedModule } from '../shared.module';
import { CitiworksAdminComponent } from './citiworks-admin/citiworks-admin.component';
import { MaintenanceComponent } from './maintenance/maintenance.component';
import { PtoSettingComponent } from './pto-setting/pto-setting.component';
import { SmtpConfigurationComponent } from './smtp-configuration/smtp-configuration.component';

type Theme = {
	label: string;
	value: string;
};

export interface GroupSubTask {
	id: number;
	name: string;
	completed: boolean;
}
export interface ReviewGroupSubTask {
	id: number;
	name: string;
	completed: boolean;
}
export interface GroupTask {
	name: string;
	completed: boolean;
	subtasks?: GroupSubTask[];
}
export interface ReviewGroupTask {
	name: string;
	completed: boolean;
	reviewSubtasks?: ReviewGroupSubTask[];
}

@Component({
	selector: 'app-profile',
	templateUrl: './profile.component.html',
	styleUrls: ['./profile.component.css'],
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		MaterialModule,
		SharedModule,
		MaintenanceComponent,
		CitiworksAdminComponent,
		SelectDropdownComponent,
		PtoSettingComponent,
		SmtpConfigurationComponent
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfileComponent implements OnInit, AfterViewInit {
	value = 'Clear me';

	userData: any;
	employeeNumber!: number;
	groupsList: any;
	reviewGroupsList: any;
	supervisorEmpList: any;

	// Transformed options for app-select-dropdown
	get groupsOptions() {
		return (
			this.groupsList?.map((group: any) => ({
				value: group.GroupID,
				label: group.GroupName
			})) || []
		);
	}

	get reviewGroupsOptions() {
		return (
			this.reviewGroupsList?.map((group: any) => ({
				value: group.ReviewGroupID,
				label: `${group.FirstName} ${group.LastName}`
			})) || []
		);
	}

	get reviewersOptions() {
		return (
			this.reviewersList?.map((reviewer: any) => ({
				value: reviewer.EmployeeNumber,
				label: `${reviewer.LastName}, ${reviewer.FirstName} (${reviewer.EmployeeNumber})`
			})) || []
		);
	}

	selectedTab: number = 0;
	currentTabLabel: string = 'Defaults';
	displayType: number = 1;

	isSupervisor: boolean = false;
	isReviewer: boolean = false;
	isMaintenanceUser: boolean = false;

	profileType1: boolean = false;
	profileType2: boolean = false;
	profileType3: string = 'summer';
	profileType4: boolean = false;
	profileType6: boolean = false;

	themes: Theme[] = [
		{
			label: 'Summer',
			value: 'summer'
		},
		{
			label: 'Winter',
			value: 'winter'
		},
		{
			label: 'Fall',
			value: 'fall'
		},
		{
			label: 'Spring',
			value: 'spring'
		},
		{
			label: 'Seasonal',
			value: 'seasonal'
		}
	];
	// 'summer', 'winter', 'fall', 'spring', 'seasonal'
	// themes: string[] = ['summer', 'winter'];
	savingSettings: boolean = false;
	settingsSaved: boolean = false;

	profileForm!: FormGroup;
	groupsForm!: FormGroup;
	reviewGroupsForm!: FormGroup;

	selectedGroupId: number = 0;
	selectedReviewGroupId: number = 0;

	showAddGroupPopup: boolean = false;
	showEditGroupPopup: boolean = false;
	groupName!: string;
	editGroupName!: string;

	showAddReviewGroupPopup: boolean = false;
	reviewer!: number;
	reviewersList: any;

	groupTask: GroupTask = {
		name: 'Select all',
		completed: false,
		subtasks: []
	};

	reviewGroupTask: ReviewGroupTask = {
		name: 'Select all',
		completed: false,
		reviewSubtasks: []
	};

	allGroupComplete: boolean = false;
	allReviewGroupComplete: boolean = false;

	profileData$: Observable<any>;

	isMaintenanceFormDirty: boolean = false;
	// Use ViewChild to get a reference to the MaintenanceComponent
	@ViewChild(MaintenanceComponent)
	maintenanceComponent!: MaintenanceComponent;
	@ViewChild(CitiworksAdminComponent)
	citiworksComponent!: CitiworksAdminComponent;
	@ViewChild(PtoSettingComponent) ptoSettingComponent!: PtoSettingComponent;
	@ViewChild(SmtpConfigurationComponent)
	smtpConfigurationComponent!: SmtpConfigurationComponent;
	isCitiworksFormDirty: boolean = false;
	isCitiworksIntegration = signal<boolean>(false);
	isPTOIntegration: boolean = false;
	cityworksAdminUserList = signal<string>(''); // Signal to track CityworksAdminUserList changes

	// Computed signal for Cityworks Admin tab visibility
	// Tab shows when: isMaintenanceUser AND isCitiworksIntegration (checkbox checked) AND current user is in CityworksAdminUserList
	shouldShowCityworksAdminTab = computed(() => {
		// Check basic conditions first - isMaintenanceUser is always required
		if (!this.isCitiworksIntegration()) {
			return false;
		}

		// Check if employee number is available
		if (!this.employeeNumber) {
			return false;
		}

		// Get CityworksAdminUserList from multiple sources (priority order)
		let adminList = '';

		// Priority 1: Signal value (updated in real-time after save)
		if (this.cityworksAdminUserList()) {
			adminList = this.cityworksAdminUserList();
		}
		// Priority 2: Maintenance component timeCardSetting (if available)
		else if (
			this.maintenanceComponent?.timeCardSetting?.CityworksAdminUserList
		) {
			adminList =
				this.maintenanceComponent.timeCardSetting
					.CityworksAdminUserList;
		}
		// Priority 3: Fallback to userData.CityworksAdminUserList (when maintenance component is not available)
		else if (this.userData?.CityworksAdminUserList) {
			adminList = this.userData.CityworksAdminUserList;
		}

		if (!adminList) {
			return false;
		}

		// Split comma-separated string and check if current employee number is in the list
		const adminIds = adminList
			.split(',')
			.map((id: string) => Number(id.trim()))
			.filter((id: number) => !isNaN(id));
		return adminIds.includes(Number(this.employeeNumber));
	});

	isPTOFormDirty: boolean = false;
	isSmtpFormDirty: boolean = false;

	private userDataSignal = signal<any>(null);

	constructor(
		private readonly fb: FormBuilder,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly toastr: ToastrService,
		private readonly authService: AuthService,
		private readonly service: ProfileService,
		public dialogRef: MatDialogRef<ProfileComponent>,
		private readonly store: Store<AppState>,
		private readonly cdr: ChangeDetectorRef
	) {
		this.authService.currentUser$.subscribe((user) => {
			this.userDataSignal.set(user); // Update signal instead of direct property
			this.userData = user;
			this.employeeNumber = user?.credentials?.EmployeeNumber;
			this.isSupervisor = this.userData?.isSupervisor;
			this.isMaintenanceUser = this.userData?.IsMaintenanceUser;
			this.isReviewer =
				this.userData?.canReviewFirstLevel === true &&
				this.userData?.tailoring?.SecondApproval !== undefined &&
				this.userData?.tailoring?.SecondApproval === true;
			// Set initial PTO integration status from user data immediately when user data is received
			if (user?.IsPTO !== undefined) {
				this.isPTOIntegration = user.IsPTO;
			}
		});

		this.profileData$ = this.store.select(selectProfileData);
	}

	ngOnInit(): void {
		this.profileForm = this.fb.group({
			profileType1: false,
			profileType2: false,
			profileType3: 'seasonal',
			profileType4: false,
			profileType6: false
		});

		// Additional fallback check in ngOnInit (in case constructor subscription hasn't fired yet)
		// if (
		// 	this.userData?.IsPTO !== undefined &&
		// 	this.isPTOIntegration !== this.userData.IsPTO
		// ) {
		// 	this.isPTOIntegration = this.userData.IsPTO;
		// }

		this.loadSavedSettings();
		this.getGroups();
		this.getReviewGroups();
	}

	ngAfterViewInit(): void {
		// Use multiple attempts to ensure we catch the integration status
		// This handles cases where API calls take different amounts of time
		this.tryGetInitialIntegrationStatus();

		// Add a final safety check after 3 seconds to ensure PTO tab shows if it should
		setTimeout(() => {
			this.performFinalPTOIntegrationCheck();
		}, 3000);
	}

	private tryGetInitialIntegrationStatus(attempt: number = 1): void {
		const maxAttempts = 10;
		const delay = attempt === 1 ? 500 : 200; // First attempt after 500ms, then 200ms intervals

		setTimeout(() => {
			let ptoStatusSet = false;
			let cityworksStatusSet = false;

			if (
				this.maintenanceComponent &&
				this.maintenanceComponent.maintenanceForm
			) {
				// Get initial PTO integration status
				const isPTOValue =
					this.maintenanceComponent.maintenanceForm.get(
						'IsPTO'
					)?.value;
				// Get initial Cityworks integration status
				const isCityworksValue =
					this.maintenanceComponent.maintenanceForm.get(
						'Cityworks'
					)?.value;

				if (
					isCityworksValue !== undefined &&
					isCityworksValue !== null
				) {
					this.checkCitiworksIntegration(isCityworksValue);
					cityworksStatusSet = true;
				}

				// Update CityworksAdminUserList signal from timeCardSetting (initial load only)
				if (
					this.maintenanceComponent.timeCardSetting
						?.CityworksAdminUserList
				) {
					this.cityworksAdminUserList.set(
						this.maintenanceComponent.timeCardSetting
							.CityworksAdminUserList
					);
				}
			}

			if (!cityworksStatusSet && this.userData?.Cityworks !== undefined) {
				this.checkCitiworksIntegration(this.userData.Cityworks);
				cityworksStatusSet = true;
			}

			// If we still don't have the status and haven't reached max attempts, try again
			if (
				(!ptoStatusSet || !cityworksStatusSet) &&
				attempt < maxAttempts
			) {
				this.tryGetInitialIntegrationStatus(attempt + 1);
			} else if (attempt >= maxAttempts) {
				// Set defaults if still not set
				// if (!ptoStatusSet) {
				// 	this.checkPTOIntegration(false);
				// }
				if (!cityworksStatusSet) {
					this.checkCitiworksIntegration(false);
				}
			}
		}, delay);
	}

	private performFinalPTOIntegrationCheck(): void {
		// Check all possible sources for PTO integration status
		let shouldShowPTOTab = false;
		let source = 'none';

		// Priority 1: Check maintenance form
		if (this.maintenanceComponent?.maintenanceForm) {
			const formPTOValue =
				this.maintenanceComponent.maintenanceForm.get('IsPTO')?.value;
			if (formPTOValue === true) {
				shouldShowPTOTab = true;
				source = 'maintenance form';
			}
		}

		// Priority 2: Check user data
		if (!shouldShowPTOTab && this.userData?.IsPTO === true) {
			shouldShowPTOTab = true;
			source = 'user data';
		}

		// Priority 3: Check current flag
		if (!shouldShowPTOTab && this.isPTOIntegration === true) {
			shouldShowPTOTab = true;
			source = 'current flag';
		}

		// Force update if there's a mismatch
		// if (shouldShowPTOTab && !this.isPTOIntegration) {
		// 	this.isPTOIntegration = true;
		// } else if (!shouldShowPTOTab && this.isPTOIntegration) {
		// 	this.isPTOIntegration = false;
		// }
	}

	changeTab(tabChangeEvent: MatTabChangeEvent) {
		let index = tabChangeEvent.index;
		if (this.selectedTab === index) {
			// Do something when the selected tab is clicked again
		} else {
			this.selectedTab = index;
		}

		// Get the tab label to determine which tab is active
		const tabLabel = tabChangeEvent.tab.textLabel;
		this.currentTabLabel = tabLabel;

		// Use tab labels instead of indices for better reliability
		switch (tabLabel) {
			case 'Groups':
				this.getSupervisorEmployees();
				break;
			case 'Review Groups':
				this.getSupervisorReviewEmployees();
				break;
			default:
				// Handle other tabs or do nothing
				break;
		}
	}

	// Helper method to check current tab type
	isCurrentTab(tabLabel: string): boolean {
		return this.currentTabLabel === tabLabel;
	}

	loadSavedSettings(): void {
		// Assuming there is a method in your service to retrieve saved settings
		this.service
			.getProfileSettings(this.employeeNumber)
			.subscribe((savedSettings) => {
				if (savedSettings) {
					let profileSetting = {
						profileType1: savedSettings.some(
							(item: any) =>
								item.ProfileTypeID === 1 &&
								item.SettingValue === 'true'
						),
						profileType2: savedSettings.some(
							(item: any) =>
								item.ProfileTypeID === 2 &&
								item.SettingValue === 'true'
						),
						profileType4: savedSettings.some(
							(item: any) =>
								item.ProfileTypeID === 4 &&
								item.SettingValue === 'true'
						),
						profileType6: savedSettings.some(
							(item: any) =>
								item.ProfileTypeID === 6 &&
								item.SettingValue === 'true'
						),
						profileType3: savedSettings.some(
							(item: any) => item.ProfileTypeID === 3
						)
					};
					// Update the form with saved settings
					this.profileForm.patchValue(profileSetting);
					const theme = savedSettings.find(
						(setting: { ProfileTypeID: number }) =>
							setting.ProfileTypeID === 3
					).SettingValue;
					// You may need to handle the theme separately as it is stored in sessionStorage
					this.profileForm.get('profileType3')?.setValue(theme);
				}
			});
	}

	saveSettings(): void {
		this.savingSettings = true;
		this.settingsSaved = false;

		const settings: any = [];

		settings.push({
			ProfileTypeID: 1,
			SettingValue: this.profileForm.get('profileType1')?.value.toString()
		});
		settings.push({
			ProfileTypeID: 2,
			SettingValue: this.profileForm.get('profileType2')?.value.toString()
		});
		settings.push({
			ProfileTypeID: 4,
			SettingValue: this.profileForm.get('profileType4')?.value.toString()
		});
		settings.push({
			ProfileTypeID: 6,
			SettingValue: this.profileForm.get('profileType6')?.value.toString()
		});

		settings.push({
			ProfileTypeID: 3,
			SettingValue: this.profileForm.get('profileType3')?.value
		});

		if (this.profileForm.get('profileType3')?.value === null) {
			this.authService.setCurrentUser({
				...this.userData,
				kiosk_theme: 'seasonal'
			});
		} else {
			this.authService.setCurrentUser({
				...this.userData,
				kiosk_theme: this.profileForm.get('profileType3')?.value
			});
		}

		this.service
			.saveProfileSettings(this.employeeNumber, settings)
			.subscribe({
				next: (d) => {
					// Assuming saveProfileSettings returns an observable, adjust accordingly
					// Also, make sure to inject and configure HttpClient in your ReviewService

					// kioskSession.addData('profile', JSON.stringify(settings)); // Commented out as kioskSession is not defined in the provided code
					this.authService.setCurrentUser({
						...this.userData,
						profile: JSON.stringify(settings)
					});

					this.savingSettings = false;
					this.settingsSaved = true;
					this.toastr.clear();
					this.toastr.success(
						'Profile UPDATED successfully.',
						'Success',
						{
							positionClass: 'toast-bottom-left',
							toastClass: 'toast-icon custom-toast-success'
						}
					);
					this.service.updateSettings(settings);
					// store data to ngrx store
					this.store.dispatch(
						ProfileActions.setProfileData({
							data: JSON.stringify(settings)
						})
					);
					this.router.navigate([], {
						relativeTo: this.route,
						queryParams: {
							// all: this.profileForm.get('profileType1')?.value ? 'yes' : 'no',
							...(this.profileForm.get('profileType1')?.value
								? { e: undefined }
								: {})
						},
						queryParamsHandling: 'merge' // keep other query params unchanged
					});
				},
				error: (err) => {
					this.toastr.clear();
					this.toastr.error('Profile updation failed!', 'Error', {
						positionClass: 'toast-bottom-left',
						toastClass: 'toast-icon custom-toast-error'
					});
				}
			});
	}

	saveGroupEmployees() {
		let selectedEmployees = '';
		this?.groupTask?.subtasks?.forEach((item, index) => {
			if (item.completed == true) {
				// Add comma if this is not the last item
				if (index > 0) {
					selectedEmployees += ',';
				}
				selectedEmployees += item.id;
			}
		});
		this.service
			.saveGroupEmployees(this.selectedGroupId, selectedEmployees)
			.subscribe({
				next: (response) => {
					this.toastr.clear();
					this.toastr.success(
						'Profile UPDATED successfully.',
						'Success',
						{
							positionClass: 'toast-bottom-left',
							toastClass: 'toast-icon custom-toast-success'
						}
					);
				},
				error: (error) => {
					this.toastr.clear();
					this.toastr.error('Profile updation failed!', 'Error', {
						positionClass: 'toast-bottom-left',
						toastClass: 'toast-icon custom-toast-error'
					});
				}
			});
	}

	saveReviewGroupEmployees() {
		let selectedEmployees = '';
		this?.reviewGroupTask?.reviewSubtasks?.forEach((item, index) => {
			if (item.completed == true) {
				// Add comma if this is not the last item
				if (index > 0) {
					selectedEmployees += ',';
				}
				selectedEmployees += item.id;
			}
		});
		this.service
			.saveReviewGroupEmployees(
				this.selectedReviewGroupId,
				selectedEmployees
			)
			.subscribe({
				next: (response) => {
					this.toastr.clear();
					this.toastr.success(
						'Profile UPDATED successfully.',
						'Success',
						{
							positionClass: 'toast-bottom-left',
							toastClass: 'toast-icon custom-toast-success'
						}
					);
				},
				error: (error) => {
					this.toastr.clear();
					this.toastr.error('Profile updation failed!', 'Error', {
						positionClass: 'toast-bottom-left',
						toastClass: 'toast-icon custom-toast-error'
					});
				}
			});
	}

	saveGroup() {
		this.editGroupName = this.groupName;
		this.service
			.saveGroup(-1, this.groupName, this.employeeNumber)
			.subscribe({
				next: (response) => {
					this.toastr.clear();
					this.toastr.success(
						'Group ADDED successfully.',
						'Success',
						{
							positionClass: 'toast-bottom-left',
							toastClass: 'toast-icon custom-toast-success'
						}
					);
					this.getGroups();

					this.selectedGroupId = response;
				},
				error: (error) => {
					this.toastr.clear();
					this.toastr.error('Something went wrong!', 'Error', {
						positionClass: 'toast-bottom-left',
						toastClass: 'toast-icon custom-toast-error'
					});
				}
			});
		this.toggleAddGroupPopup();
	}

	saveReviewGroup() {
		this.service
			.saveReviewGroup(this.employeeNumber, this.reviewer)
			.subscribe({
				next: (response) => {
					this.toastr.clear();
					this.toastr.success(
						'Review Group ADDED successfully.',
						'Success',
						{
							positionClass: 'toast-bottom-left',
							toastClass: 'toast-icon custom-toast-success'
						}
					);
					this.getReviewGroups();

					this.selectedReviewGroupId = response;
				},
				error: (error) => {
					this.toastr.clear();
					this.toastr.error('Something went wrong!', 'Error', {
						positionClass: 'toast-bottom-left',
						toastClass: 'toast-icon custom-toast-error'
					});
				}
			});
		this.toggleAddReviewGroupPopup();
	}

	updateGroup() {
		this.service
			.saveGroup(
				this.selectedGroupId,
				this.editGroupName,
				this.employeeNumber
			)
			.subscribe({
				next: (response) => {
					this.toastr.clear();
					this.toastr.success(
						'Group UPDATED successfully.',
						'Success',
						{
							positionClass: 'toast-bottom-left',
							toastClass: 'toast-icon custom-toast-success'
						}
					);
					this.getGroups();
					// this.selectedGroupId = response;
				},
				error: (error) => {
					this.toastr.clear();
					this.toastr.error('Something went wrong!', 'Error', {
						positionClass: 'toast-bottom-left',
						toastClass: 'toast-icon custom-toast-error'
					});
				}
			});
		this.toggleEditGroupPopup();
	}

	deleteGroup() {
		this.service.deleteGroup(this.selectedGroupId).subscribe({
			next: (response) => {
				this.toastr.clear();
				this.toastr.success('Group DELETED successfully.', 'Success', {
					positionClass: 'toast-bottom-left',
					toastClass: 'toast-icon custom-toast-success'
				});
				this.getGroups();
				this.groupTask.completed = false;
				this.allGroupComplete = false;
				this.selectedGroupId = 0;
				this?.groupTask?.subtasks?.forEach((item) => {
					item.completed = false;
				});
			},
			error: (error) => {
				this.toastr.clear();
				this.toastr.error('Something went wrong!', 'Error', {
					positionClass: 'toast-bottom-left',
					toastClass: 'toast-icon custom-toast-error'
				});
			}
		});
	}

	deleteReviewGroup() {
		this.service.deleteReviewGroup(this.selectedReviewGroupId).subscribe({
			next: (response) => {
				this.toastr.clear();
				this.toastr.success(
					'Review Group DELETED successfully.',
					'Success',
					{
						positionClass: 'toast-bottom-left',
						toastClass: 'toast-icon custom-toast-success'
					}
				);
				this.getReviewGroups();
				this.reviewGroupTask.completed = false;
				this.allReviewGroupComplete = false;
				this.selectedReviewGroupId = 0;
				this?.reviewGroupTask?.reviewSubtasks?.forEach((item) => {
					item.completed = false;
				});
			},
			error: (error) => {
				this.toastr.clear();
				this.toastr.error('Something went wrong!', 'Error', {
					positionClass: 'toast-bottom-left',
					toastClass: 'toast-icon custom-toast-error'
				});
			}
		});
	}

	getGroups() {
		this.service.getGroups(this.employeeNumber).subscribe({
			next: (response) => {
				this.groupsList = response;
			},
			error: (error) => {
				console.log('getGroups error : ', error);
			}
		});
	}

	getSupervisorEmployees() {
		this.service.getSupervisorEmployees(this.employeeNumber).subscribe({
			next: (response) => {
				this.supervisorEmpList = response;
				response.forEach(
					(employee: {
						EmployeeNumber: number;
						LastName: string;
						FirstName: string;
					}) => {
						let empObj = {
							id: employee.EmployeeNumber,
							name: `${employee.LastName}, ${employee.FirstName} (${employee.EmployeeNumber})`,
							completed: false
						};
						this.groupTask.subtasks?.push(empObj);
						// this.reviewGroupTask.reviewSubtasks?.push(empObj);
					}
				);
			},
			error: (error) => {
				console.log('Fetching Supervisor Employees error :', error);
			}
		});
	}

	getSupervisorReviewEmployees() {
		let supervisorNo = this.employeeNumber;
		if (this.isReviewer) {
			supervisorNo = this.reviewGroupsList[0].CreatedBy;
		}
		this.service.getSupervisorEmployees(supervisorNo).subscribe({
			next: (response) => {
				this.supervisorEmpList = response;
				response.forEach(
					(employee: {
						EmployeeNumber: number;
						LastName: string;
						FirstName: string;
					}) => {
						let empObj = {
							id: employee.EmployeeNumber,
							name: `${employee.LastName}, ${employee.FirstName} (${employee.EmployeeNumber})`,
							completed: false
						};
						// this.groupTask.subtasks?.push(empObj);
						this.reviewGroupTask.reviewSubtasks?.push(empObj);
					}
				);
			},
			error: (error) => {
				console.log('get Supervisor Employees error :', error);
			}
		});
	}

	onGroupSelectionChange(value: number | null) {
		if (value && value !== 0) {
			const selectedGroup = this.groupsList?.find(
				(g: any) => g.GroupID === value
			);
			if (selectedGroup) {
				this.getGroupEmployees(selectedGroup);
			}
		} else {
			this.selectedGroupId = 0;
			this.groupTask.completed = false;
			this.allGroupComplete = false;
			this?.groupTask?.subtasks?.forEach((item) => {
				item.completed = false;
			});
		}
	}

	getGroupEmployees(Group: any) {
		this.selectedGroupId = Group.GroupID;
		this.editGroupName = Group.GroupName;
		this.service.getGroupEmployees(this.selectedGroupId).subscribe({
			next: (response) => {
				// response.forEach((id: any) => {
				//   let index = this?.task?.subtasks?.findIndex(item => item.id === id);
				//   if (index !== -1) {
				//     this?.task?.subtasks?[index].completed = true;
				//   }
				// });
				this.allGroupComplete = true;
				this?.groupTask?.subtasks?.forEach((item) => {
					if (response.includes(item.id)) {
						item.completed = true;
					} else {
						item.completed = false;
						this.allGroupComplete = false;
					}
				});
			},
			error: (error) => {
				console.log('getGroupEmployees error : ', error);
			}
		});
	}

	getReviewGroups() {
		this.service.getReviewGroups(this.employeeNumber).subscribe({
			next: (response) => {
				if (this.isReviewer) {
					this.reviewGroupsList = response.filter(
						(group: { Reviewer: any }) =>
							+group.Reviewer === +this.employeeNumber
					);
				} else {
					this.reviewGroupsList = response;
				}
			},
			error: (error) => {
				console.log('getReviewGroups error : ', error);
			}
		});
	}

	onReviewGroupSelectionChange(value: number | null) {
		if (value && value !== 0) {
			this.getReviewGroupEmployees(value);
		} else {
			this.selectedReviewGroupId = 0;
			this.reviewGroupTask.completed = false;
			this.allReviewGroupComplete = false;
			this?.reviewGroupTask?.reviewSubtasks?.forEach((item) => {
				item.completed = false;
			});
		}
	}

	getReviewGroupEmployees(GroupID: number) {
		this.selectedReviewGroupId = GroupID;
		this.service.getReviewGroupEmployees(GroupID).subscribe({
			next: (response) => {
				this?.reviewGroupTask?.reviewSubtasks?.forEach((item) => {
					if (response.includes(item.id)) {
						item.completed = true;
					} else {
						item.completed = false;
						this.allReviewGroupComplete = false;
					}
				});
			},
			error: (error) => {
				console.log('getReviewGroupEmployees error : ', error);
			}
		});
	}

	closeProfileDialog(): void {
		this.dialogRef.close();
	}

	updateAllCompleteGroupTask() {
		this.allGroupComplete =
			this.groupTask.subtasks != null &&
			this.groupTask.subtasks.every((t) => t.completed);
	}

	onGroupItemClick(checked: boolean, item: any): void {
		item.subtasks = checked;
		this.updateAllCompleteGroupTask();
	}

	onReviewGroupItemClick(checked: boolean, item: any): void {
		item.reviewSubtasks = checked;
		this.updateAllCompleteReviewGroupTask();
	}

	someCompleteGroupTask(): boolean {
		if (this.groupTask.subtasks == null) {
			return false;
		}
		return (
			this.groupTask.subtasks.filter((t) => t.completed).length > 0 &&
			!this.allGroupComplete
		);
	}

	setAllGroupTask(completed: boolean) {
		this.allGroupComplete = completed;
		if (this.groupTask.subtasks == null) {
			return;
		}
		this.groupTask.subtasks.forEach((t) => (t.completed = completed));
	}

	updateAllCompleteReviewGroupTask() {
		this.allReviewGroupComplete =
			this.reviewGroupTask.reviewSubtasks != null &&
			this.reviewGroupTask.reviewSubtasks.every((t) => t.completed);
	}

	someCompleteReviewGroupTask(): boolean {
		if (this.reviewGroupTask.reviewSubtasks == null) {
			return false;
		}
		return (
			this.reviewGroupTask.reviewSubtasks.filter((t) => t.completed)
				.length > 0 && !this.allReviewGroupComplete
		);
	}

	setAllReviewGroupTask(completed: boolean) {
		this.allReviewGroupComplete = completed;
		if (this.reviewGroupTask.reviewSubtasks == null) {
			return;
		}
		this.reviewGroupTask.reviewSubtasks.forEach(
			(t) => (t.completed = completed)
		);
	}

	toggleAddGroupPopup() {
		this.showAddGroupPopup = !this.showAddGroupPopup;
		this.groupName = '';
	}

	toggleEditGroupPopup() {
		this.showEditGroupPopup = !this.showEditGroupPopup;
		// this.editGroupName = '';
	}

	toggleAddReviewGroupPopup() {
		this.showAddReviewGroupPopup = !this.showAddReviewGroupPopup;
		this.reviewer = 0;
		const reviewerIds = this.reviewGroupsList.map(
			(group: { Reviewer: any }) => group.Reviewer
		);
		this.reviewersList = this.supervisorEmpList.filter(
			(employee: {
				EmployeeNumber: {
					toString: () => any;
				};
			}) => {
				const employeeIdString = employee.EmployeeNumber;
				return !reviewerIds.includes(employeeIdString);
			}
		);
	}

	checkMaintenanceFormState(isDirty: boolean | any) {
		this.isMaintenanceFormDirty = isDirty;
		// Update CityworksAdminUserList signal only when form is saved (isDirty becomes false)
		// Update from timeCardSetting which is updated after save in maintenance component
		if (!isDirty && this.maintenanceComponent?.timeCardSetting) {
			this.cityworksAdminUserList.set(
				this.maintenanceComponent.timeCardSetting
					.CityworksAdminUserList || ''
			);
		}
	}

	saveMaintenanceData() {
		// Check if the form is valid and call the child component's save method
		if (this.isMaintenanceFormDirty) {
			this.maintenanceComponent.saveMaintenance(); // Calls the saveData method in the child component
			// Update CityworksAdminUserList signal after save from timeCardSetting
			setTimeout(() => {
				if (
					this.maintenanceComponent?.timeCardSetting
						?.CityworksAdminUserList !== undefined
				) {
					this.cityworksAdminUserList.set(
						this.maintenanceComponent.timeCardSetting
							.CityworksAdminUserList || ''
					);
				}
			}, 300);
		}
	}

	checkCitiworksIntegration(citiworksIntegration: boolean) {
		this.isCitiworksIntegration.set(citiworksIntegration);
	}

	onCityworksAdminUserListUpdated(cityworksAdminUserList: string) {
		// Update signal immediately when CityworksAdminUserList is updated after save
		this.cityworksAdminUserList.set(cityworksAdminUserList || '');
	}

	checkPTOIntegration(isPTOIntegration: boolean) {
		const previousValue = this.isPTOIntegration;
		this.isPTOIntegration = isPTOIntegration;

		// Force change detection if the value changed
		if (previousValue !== isPTOIntegration) {
			setTimeout(() => {
				// Additional change detection to ensure UI updates
				if (this.isPTOIntegration !== isPTOIntegration) {
					this.isPTOIntegration = isPTOIntegration;
				}
			}, 50);
		}
	}

	saveCitiworksData() {
		// Check if the form is valid and call the child component's save method
		if (this.isCitiworksFormDirty) {
			this.citiworksComponent.onSaveCitiworks(); // Calls the saveData method in the child component
		}
	}

	checkPtoFormState(isDirty: boolean | any) {
		this.isPTOFormDirty = isDirty;
	}

	savePtoSetting() {
		// Check if the form is valid and call the child component's save method
		if (this.isPTOFormDirty) {
			this.ptoSettingComponent.savePTO(); // Calls the saveData method in the child component
		}
	}

	checkSmtpFormState(isDirty: boolean | any) {
		this.isSmtpFormDirty = isDirty;
	}

	saveSmtpConfiguration() {
		// Check if the form is valid and call the child component's save method
		if (this.isSmtpFormDirty) {
			this.smtpConfigurationComponent.saveSmtpConfiguration(); // Calls the save method in the child component
		}
	}
}
